import { useEffect, useRef, useState } from "react";

/**
 * Example:
 *    // In this case, both events share same timeout handler.
 *    // and every call of witch one reset time.
 *    const debouncedRef = useCreateDebouncedRef(500);
 *    const handleEventOne = useDebouncedCallback(debouncedRef, () => {
 *      // Run in each call.
 *      return () => {
 *        // Run at end of 500ms.
 *      };
 *    }, [deps]);
 * 
 *    const handleEventTwo = useDebouncedCallback(debouncedRef, () => {
 *      // Run in each call.
 *      return () => {
 *        // Run at end of 500ms.
 *      };
 *    }, [deps]);
 */

/**
 * Prim types
 */

type TDebouncedRef = { timeoutRef: React.MutableRefObject<any>, time: number };
type TDisposeEffectCallback = (() => void) | Promise<() => void>;
type TFunctionCallback<T extends any[],> = (...args: T) => TDisposeEffectCallback;
type TReturnFunctionCallback<T extends any[],> = (...args: T) => void;
type TDependencyList = readonly unknown[];

/**
 * checkDependenciesChanged
 */

const checkDependenciesChanged = (
  prevDeps: TDependencyList,
  deps: TDependencyList
): boolean => {
  if (prevDeps.length !== deps.length) {
    console.warn("The length of the dependencies array must remain constistent between renders.");
  }
  return deps.some((dep, index) => !Object.is(dep, prevDeps[index]));
};

/**
 * useCreateDebounceHandler
 */

export const useCreateDebounceHandler = (time: number): TDebouncedRef => {
  const timeoutRef = useRef<any>(null);
  const [handler, setHandler] = useState<TDebouncedRef>({ timeoutRef, time });
  useEffect(() => { setHandler({ timeoutRef, time }) }, [time]);
  return handler;
};

/**
 * useDebounceEffect
 */

export const useDebouncedCallback = <T extends any[],>(
  debouncedRef: TDebouncedRef,
  callback: TFunctionCallback<T>,
  deps?: TDependencyList,
): TReturnFunctionCallback<T> => {
  const isFirstRender = useRef(true);
  const functionCallback = useRef(callback);
  const cachedRetFunctionCallback = useRef<TReturnFunctionCallback<T> | null>(null);
  const prevDeps = useRef(deps);

  if (isFirstRender.current) {
    isFirstRender.current = false;
    prevDeps.current = deps;
    cachedRetFunctionCallback.current = (...args: T) => {
      const disposableFn = functionCallback.current(...args);
      window.clearTimeout(debouncedRef.timeoutRef.current);
      if (typeof disposableFn === 'function') {
        debouncedRef.timeoutRef.current = window.setTimeout(disposableFn, debouncedRef.time);
      } else if (disposableFn instanceof Promise) {
        disposableFn.then((x) => debouncedRef.timeoutRef.current = window.setTimeout(x, debouncedRef.time));
      }
    };
    return cachedRetFunctionCallback.current;
  }

  if (!deps) {
    cachedRetFunctionCallback.current = (...args: T) => {
      const disposableFn = functionCallback.current(...args);
      window.clearTimeout(debouncedRef.timeoutRef.current);
      if (typeof disposableFn === 'function') {
        debouncedRef.timeoutRef.current = window.setTimeout(disposableFn, debouncedRef.time);
      } else if (disposableFn instanceof Promise) {
        disposableFn.then((x) => debouncedRef.timeoutRef.current = window.setTimeout(x, debouncedRef.time));
      }
    };
    return cachedRetFunctionCallback.current;
  }

  if (!prevDeps.current) throw new Error('Invalid cached return function callback!');

  if (checkDependenciesChanged(prevDeps.current, deps)) {
    functionCallback.current = callback;
    cachedRetFunctionCallback.current = (...args: T) => {
      const disposableFn = functionCallback.current(...args);
      window.clearTimeout(debouncedRef.timeoutRef.current);
      if (typeof disposableFn === 'function') {
        debouncedRef.timeoutRef.current = window.setTimeout(disposableFn, debouncedRef.time);
      } else if (disposableFn instanceof Promise) {
        disposableFn.then((x) => debouncedRef.timeoutRef.current = window.setTimeout(x, debouncedRef.time));
      }
    };
  }

  if (!cachedRetFunctionCallback.current) {
    throw new Error('Invalid cached return function callback!');
  }

  prevDeps.current = deps;
  return cachedRetFunctionCallback.current;
}